/**
 * Placing one authored asset many times without paying for it many times.
 *
 * A dressed farrowing hall is the same eight objects repeated forty times. Doing
 * that with `asset.create()` in a loop is correct, readable, and costs a draw
 * call per mesh per placement — a floor of 1.2 m slat tiles over a 20 m hall is
 * a hundred draw calls on its own, and spec §1 budgets four hundred for the
 * whole frame.
 *
 * This does two things, and the second one turned out to matter more:
 *
 * 1. **Instancing.** One `InstancedMesh` covers every placement.
 * 2. **Merging by surface.** All the source's meshes that share a surface are
 *    merged into one geometry first. Without this, an asset built from fifty
 *    named pipes still costs fifty draw calls no matter how well it is
 *    instanced — and the farrowing crate was exactly that, at 106 calls for a
 *    single object the hall places sixteen of.
 *
 * The seam is deliberately narrow: hand it an asset group and a list of
 * placements, get back a group to add to the scene. Nothing about authoring an
 * asset changes — `docs/asset-conventions.md` still holds, factories stay pure,
 * and the module is still previewable on its own in the workbench.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Where one copy goes. Rotation is about Y only; nothing in the kit tilts. */
export interface Placement {
  x: number;
  z: number;
  /** Height above the floor. Almost always 0 — origins are at ground contact. */
  y?: number;
  /** Yaw in radians. */
  rotY?: number;
  /**
   * Pitch in radians, applied before yaw.
   *
   * Exists for one honest reason: assets are authored with their origin at
   * ground contact (`docs/asset-conventions.md`), so a ceiling fitting is
   * modelled standing on the floor and has to be turned over to be mounted.
   * Rotating the asset is right; authoring it upside down so it "just works" in
   * one room would break it in the workbench and in every other room.
   */
  rotX?: number;
  /**
   * Roll in radians, applied before pitch and yaw (Euler order `YXZ`).
   *
   * Exists for one object: the pen wall the fattening hall has **lifted out and
   * left leaning against the next one**, which is how beat 3 says "nobody is
   * using this pen" without a word or a prompt. A leaning panel is a roll in the
   * asset's own frame, and doing it by hand outside the instancer would cost the
   * merge — which for a panel with three surfaces is three draw calls to tilt one
   * board.
   */
  rotZ?: number;
}

interface Bucket {
  material: THREE.Material;
  geometries: THREE.BufferGeometry[];
  castShadow: boolean;
  receiveShadow: boolean;
  name: string;
}

/**
 * "Same surface" for batching purposes.
 *
 * `surfaceKey` is stamped on by `toonMaterial()`. The uuid fallback is correct
 * rather than lazy: anything without the stamp is not a toon material, and
 * merging two of those on a guess is how you get a room painted one colour.
 */
function surfaceKeyOf(material: THREE.Material): string {
  // `userData` is typed `any`, so it is narrowed here rather than trusted.
  const key = (material.userData as Record<string, unknown>)['surfaceKey'];
  return typeof key === 'string' ? key : material.uuid;
}

/**
 * One merged geometry per surface, in the frame the result will be placed in.
 *
 * Split out of `instanceGroup` for the hen flock, which needs the merge without
 * the baked placements: eight birds that move independently cannot have their
 * transforms baked into a geometry, but they can share one `InstancedMesh` per
 * surface whose per-instance matrices are rewritten every frame. That is the
 * only way a flock fits in a room with thirteen draw calls of headroom — see
 * `henFlock.ts`, which is the reason this seam exists at all.
 *
 * The source group is consumed for its geometry and materials and is not added
 * to the scene.
 */
export function mergeBySurface(source: THREE.Object3D): SurfaceBatch[] {
  // Sub-mesh transforms baked into the frame the result will be placed in,
  // which is what "baked in" has always claimed to mean here and did not.
  //
  // ── The bug this replaces, because it shipped and it was expensive ──────────
  //
  // This used to be `updateWorldMatrix(false, true)` followed by baking
  // `mesh.matrixWorld` straight into the geometry. `updateWorldMatrix` ignores
  // its first argument for the node's OWN matrix — it still composes against
  // `this.parent.matrixWorld` — so when `source` was already in the scene the
  // baked matrices were world-space. Every call site that then added the result
  // back underneath a transformed node applied that transform a second time.
  //
  // For a detached source, which is every call in `dressFarrowingHall`, world
  // space and source space are the same thing and nothing was ever wrong. For
  // `collapse()`, which merges a subtree *in place* under a node
  // that is positioned and rotated, it was catastrophic and completely silent:
  //
  //   - **Her mother** was baked at her world position and then rotated a
  //     further 90° about the origin and translated again. Her measured bounds
  //     were 10.4 m × 0.55 m × 2.3 m — a sow smeared the length of the hall,
  //     with pieces of her standing in other people's pens. The human review of
  //     beat 1 said *"I did not see the mother sow… there is something in the
  //     middle attached to the wall which could be the mother but I cannot reach
  //     her in any way"*, and that is exactly what he was looking at.
  //   - **The boots**, parked at z −12.2, doubled to roughly z −24 — outside the
  //     hall. The review said *"I did not recognize the boots"*. There were none
  //     to recognise.
  //   - **The heat lamp** went with them, which is why the one warm light in the
  //     zone had no fitting under it.
  //
  // Typecheck, lint and 107 tests passed throughout. It was found by measuring a
  // bounding box, not by reading anything — the comment on `collapse()` stated
  // the opposite of the truth with complete confidence, which is now this
  // project's most frequent defect and its fourth appearance in this file's
  // neighbourhood.
  // ── Which frame, exactly ─────────────────────────────────────────────────
  //
  // The frame the RESULT will be placed in, which is the source's **parent**,
  // not the source. That distinction is the whole of this function's history:
  //
  //   - `collapse()` passes a holder that is a child of the live node, and adds
  //     the result back under that same node. Parent frame = the node. Baking
  //     the node's world transform in was the double-transform bug above.
  //   - `structure()` in `farrowing.ts` passes a bare `Mesh` that carries its own
  //     `position.y = HALL.ceiling - 0.08`, with placements that only give x
  //     and z, and adds the result to a group at the origin. Parent frame = the
  //     origin, and **the beam's own offset is part of what must be kept.**
  //
  // Taking it relative to `source` gets the first right and the second wrong:
  // for a single-mesh source, `inverse(source.matrixWorld) × mesh.matrixWorld`
  // is the identity, so the ceiling height was divided straight out and eight
  // ceiling beams were baked at y = 0, on the floor. Found on independent
  // review — of the fix, not of the original bug, which is the point of running
  // one (D-035).
  //
  // Relative to the parent gets both right, and needs no special case: a
  // detached source has no parent, so the transform stays exactly as authored.
  source.updateWorldMatrix(true, true);
  const parentWorld = source.parent?.matrixWorld;
  const toPlacementSpace = new THREE.Matrix4();
  if (parentWorld) toPlacementSpace.copy(parentWorld).invert();
  const localMatrix = new THREE.Matrix4();

  const buckets = new Map<string, Bucket>();

  source.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    // Multi-material meshes would need a merge that preserves groups, and no
    // asset in this project has one. Refusing loudly beats silently painting it
    // with whichever material happened to be first.
    if (Array.isArray(mesh.material)) {
      throw new Error(`instanceGroup: "${mesh.name}" has multiple materials, which is not supported`);
    }

    const key = surfaceKeyOf(mesh.material);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        material: mesh.material,
        geometries: [],
        castShadow: false,
        receiveShadow: false,
        name: mesh.name || 'mesh',
      };
      buckets.set(key, bucket);
    }
    // Cloned, because the source geometry may be shared between sub-meshes and
    // baking a transform into it in place would move the others.
    localMatrix.multiplyMatrices(toPlacementSpace, mesh.matrixWorld);
    const geometry = mesh.geometry.clone().applyMatrix4(localMatrix);
    // A `uv` nothing will ever sample, so that hand-built geometry can be merged
    // with a `BoxGeometry`.
    //
    // **This cost this session two debug cycles and it would have cost every
    // future one.** `mergeGeometries` requires an identical attribute set across
    // its inputs. Three's primitives all carry position, normal and uv; a
    // hand-rolled lofted body carries position and a computed normal and nothing
    // else. Mix the two in one kit piece — which is what any asset with a
    // sculpted body and a box detail is — and the merge fails, and it fails at
    // the ROOM: the hall refuses to boot, with an error naming a sub-mesh nobody
    // has heard of.
    //
    // Supplying it here rather than in each asset is right because the
    // requirement is this function's, not the asset author's: **nothing in this
    // project is textured.** Every material comes from `surface()` and every
    // surface is flat colour, so a uv is pure merge ceremony and an asset author
    // should not have to know that it exists. `docs/asset-conventions.md` says
    // they should not have to know this function exists at all.
    if (!geometry.getAttribute('uv') && geometry.getAttribute('position')) {
      const count = geometry.getAttribute('position').count;
      geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(count * 2), 2));
    }
    bucket.geometries.push(geometry);
    // Any piece casting or receiving means the merged whole does. Merging
    // collapses the distinction, and losing a contact shadow is far more visible
    // than paying for one that was not strictly needed.
    bucket.castShadow ||= mesh.castShadow;
    bucket.receiveShadow ||= mesh.receiveShadow;
  });

  const batches: SurfaceBatch[] = [];
  for (const bucket of buckets.values()) {
    const merged =
      bucket.geometries.length === 1
        ? bucket.geometries[0]
        : mergeGeometries(bucket.geometries, false);
    if (!merged) {
      throw new Error(
        `mergeBySurface: could not merge ${String(bucket.geometries.length)} geometries for "${bucket.name}" — ` +
          'they probably do not share the same set of vertex attributes',
      );
    }
    // The clones have been copied into the merge and are otherwise unreachable.
    if (bucket.geometries.length > 1) {
      for (const g of bucket.geometries) g.dispose();
    }
    batches.push({
      geometry: merged,
      material: bucket.material,
      castShadow: bucket.castShadow,
      receiveShadow: bucket.receiveShadow,
      name: bucket.name,
    });
  }
  return batches;
}

/** One surface's worth of an asset, merged. See `mergeBySurface`. */
export interface SurfaceBatch {
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.Material;
  readonly castShadow: boolean;
  readonly receiveShadow: boolean;
  readonly name: string;
}

/** A static cost record for the render-budget harness; never logged at runtime. */
export interface InstanceGroupCensus {
  readonly name: string;
  readonly instanceCount: number;
  readonly sourceTriangles: number;
  readonly submittedTriangles: number;
  readonly materialGroups: number;
}

function triangleCount(geometry: THREE.BufferGeometry): number {
  const positions = geometry.getAttribute('position');
  if (!positions) return 0;
  return (geometry.index?.count ?? positions.count) / 3;
}

/**
 * Instance `source` at every placement.
 *
 * The source group is consumed for its geometry and materials and is not added
 * to the scene. Its sub-mesh transforms are baked in, so a kit piece built as
 * five nested pivots instances exactly as well as one built flat — which
 * matters, because an asset author should not have to know this function exists.
 */
export function instanceGroup(
  source: THREE.Object3D,
  placements: readonly Placement[],
  name = 'instances',
): THREE.Group {
  const group = new THREE.Group();
  group.name = name;

  if (placements.length === 0) return group;
  const matrices = placements.map((p) => {
    const m = new THREE.Matrix4();
    m.compose(
      new THREE.Vector3(p.x, p.y ?? 0, p.z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(p.rotX ?? 0, p.rotY ?? 0, p.rotZ ?? 0, 'YXZ')),
      new THREE.Vector3(1, 1, 1),
    );
    return m;
  });

  const batches = mergeBySurface(source);
  const sourceTriangles = batches.reduce((total, batch) => total + triangleCount(batch.geometry), 0);
  const census: InstanceGroupCensus = {
    name,
    instanceCount: placements.length,
    sourceTriangles,
    submittedTriangles: sourceTriangles * placements.length,
    materialGroups: batches.length,
  };
  group.userData['instanceGroupCensus'] = census;

  for (const batch of batches) {
    const instanced = new THREE.InstancedMesh(batch.geometry, batch.material, placements.length);
    instanced.name = `${batch.name}-x${String(placements.length)}`;
    instanced.castShadow = batch.castShadow;
    instanced.receiveShadow = batch.receiveShadow;

    for (let i = 0; i < matrices.length; i++) {
      const m = matrices[i];
      if (m) instanced.setMatrixAt(i, m);
    }
    instanced.instanceMatrix.needsUpdate = true;
    // Without this the whole batch is culled by the bounding sphere of a single
    // instance sitting at the asset origin, and the room vanishes the moment the
    // camera looks away from world zero.
    instanced.computeBoundingSphere();

    group.add(instanced);
  }

  return group;
}


/**
 * Replace a node's whole mesh subtree with one merged mesh per surface, keeping
 * the node itself live so it can still be animated.
 *
 * The farrowing hall was 189 draw calls before this bay was added to it and 347
 * after — against spec §1's 400 for the WHOLE frame, and against `m0b.spec.ts`'s
 * stricter 200 for the room alone. Nothing here is instanced (every object is
 * unique) but almost everything shares a handful of materials, and each is paid
 * for three times over: shadow pass, normal prepass, beauty pass. Her mother
 * alone is thirty-two named meshes in four materials.
 *
 * `instanceGroup` at a single placement is the merge without the instancing,
 * which is exactly what unique static scenery wants. The node keeps its identity
 * and its transform; only its children collapse.
 */
export function collapse(node: THREE.Object3D, name: string): void {
  // The meshes are re-parented with `attach` into a temporary child of `node`.
  // That turns their transforms into `node`-relative local matrices. The holder
  // is then detached before `instanceGroup` reads those matrices: passing it on
  // while still parented would bake node's world transform and apply it a second
  // time when the merged result goes back under node.
  //
  // That sentence was false for as long as it has existed, and it is worth
  // saying where the falsehood lived: it was true of what `collapse` does here
  // and NOT of what `instanceGroup` did with the result, which baked world
  // matrices and so had `node`'s transform applied to it twice. Her mother came
  // out 10.4 m long and spread across the hall. The fix is in `instancing.ts`
  // and the argument is written out there; this comment is now accurate because
  // that one changed, not because this one did.
  //
  // The first version moved `node.children` into a detached holder instead. That
  // is a different operation and it was catastrophic: collapsing her `breath`
  // node carried `lie` and `standing` out of the scene with it, so the head
  // pivot — which had been detached first, and was about to be re-attached to
  // `standing` — was re-attached to a subtree nobody could reach. **Her mother
  // rendered without a head**, and the comment beside the code confidently
  // explained why that could not happen. It shipped, and it was found by an
  // independent review, not by me: I regenerated the screenshots after this
  // change and did not look at them again.
  node.updateMatrixWorld(true);
  const holder = new THREE.Group();
  node.add(holder);
  const meshes: THREE.Mesh[] = [];
  node.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) meshes.push(mesh);
  });
  if (meshes.length === 0) {
    node.remove(holder);
    return;
  }
  // Collected before re-parenting: `traverse` walks a tree that `attach` is
  // about to rewrite.
  for (const mesh of meshes) holder.attach(mesh);
  node.remove(holder);
  // Its children already hold the node-relative transforms `attach` calculated.
  // Detached, their matrixWorld values now become exactly those local matrices
  // when `instanceGroup` updates the holder, which is the frame the merged mesh
  // must be baked in before it returns under `node`.
  const merged = instanceGroup(holder, [{ x: 0, z: 0 }], name);
  node.add(merged);
}

/**
 * Prevent a static collapse from severing a rig's animated geometry.
 *
 * A whole-tree collapse leaves a named pivot in place but carries the geometry
 * it was supposed to move into an ancestor's merged mesh. Animation then still
 * resolves every rig name and silently moves nothing. Beat 4 pays for four
 * figures in three render passes, so preserving the rig boundary is what lets
 * the draw-call fix remain a rendering change rather than an animation bug.
 */
export function collapseRigged(root: THREE.Object3D, pivots: readonly THREE.Object3D[]): void {
  const liveNodes = new Set<THREE.Object3D>([root, ...pivots]);
  const depths = new Map<THREE.Object3D, number>();

  const depthFromRoot = (node: THREE.Object3D): number => {
    const known = depths.get(node);
    if (known !== undefined) return known;

    let depth = 0;
    let current: THREE.Object3D | null = node;
    while (current && current !== root) {
      current = current.parent;
      depth += 1;
    }
    if (!current) throw new Error(`collapseRigged: live node "${node.name}" is outside root "${root.name}"`);
    depths.set(node, depth);
    return depth;
  };

  const ordered = [...liveNodes].sort((a, b) => depthFromRoot(b) - depthFromRoot(a));

  for (const node of ordered) {
    const directLiveChildren = [...liveNodes].filter((candidate) => {
      if (candidate === node) return false;

      let ancestor = candidate.parent;
      while (ancestor && ancestor !== node) {
        if (liveNodes.has(ancestor)) return false;
        ancestor = ancestor.parent;
      }
      return ancestor === node;
    });

    const detached = directLiveChildren.map((child) => {
      const parent = child.parent;
      if (!parent) {
        throw new Error(`collapseRigged: live node "${child.name}" lost its parent`);
      }
      parent.remove(child);
      return { child, parent };
    });

    try {
      collapse(node, `${node.name || 'rig'}-static`);
    } finally {
      // The original parent remains in the tree: putting the joint back there
      // retains its bind-space transform without letting a detached holder hide
      // it from the next animation lookup.
      for (const { child, parent } of detached) parent.add(child);
    }
  }
}
